import React from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { Icon } from '@shared/ui';
import { isFileWithNestedName, isHasName } from '@shared/ui/DnD/lib/typeGuards';

import { Attachments } from './Attachments';
import cls from './DnD.module.css';
import { useDnd } from '../lib/hooks/useDnd';

interface IDnDProps<T extends { id: number }> {
    attachmentsData: T[];
    onUploadHandler: (files: File[]) => void;
    fileInputRef: React.MutableRefObject<HTMLInputElement>;
    isDisabled?: boolean;
    containerClassName?: string;
    renderAttachmentsList?: () => React.ReactNode | null;
    addFileTitle?: string;
    multiple?: boolean;
    accept?: string;
    error?: null | string;
    inputIcon?: React.ReactNode;
}

export const DnD = <T extends { id: number }>({
    attachmentsData,
    onUploadHandler,
    renderAttachmentsList = null,
    isDisabled,
    fileInputRef,
    containerClassName,
    addFileTitle,
    multiple = true,
    accept,
    error,
    inputIcon,
}: IDnDProps<T>) => {
    const { t } = useTranslation();
    const {
        attachments,
        isFileOver,
        handleDragOver,
        handleDragEnter,
        handleDragLeave,
        handleDnDClick,
        handleInputChange,
        handleDrop,
        clickFileInput,
    } = useDnd<T>({ isDisabled, attachmentsData, onUploadHandler, fileInputRef });

    return (
        <div
            className={cn(
                cls.Container,
                attachments.length > 0 && cls.hasAttachments,
                isFileOver && cls.dragOver,
                isDisabled && cls.disabled,
                error && cls.error,
                containerClassName
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
        >
            <div
                onDragEnter={handleDragEnter}
                className={cn(cls.DnD, isFileOver && cls.onDragOver)}
                onClick={handleDnDClick}
                onDrop={handleDrop}
            >
                {attachments.length === 0 && (
                    <div className={cn(cls.ActiveUploadArea)}>
                        <div className={cls.AddFilesTitleWrapper}>
                            {inputIcon || <Icon iconName="fileInput" />}
                            <div className={cn(cls.AddFileTitle, isFileOver && cls.FileOve)}>
                                {error && !isFileOver ? (
                                    error
                                ) : (
                                    <>
                                        {isFileOver &&
                                            t('Tasks.ModalFileUploader.DnD.isFileOver.text')}
                                        {!isFileOver &&
                                            !isDisabled &&
                                            (addFileTitle ||
                                                t('Tasks.ModalFileUploader.DnD.AddFile.text'))}
                                        {!isFileOver &&
                                            isDisabled &&
                                            t('Tasks.ModalFileUploader.DnD.noFiles.text')}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {attachmentsData.length > 0 && (
                    <div className={cn(cls.AttachmentsListWrapper, isFileOver && cls.isFileOver)}>
                        {renderAttachmentsList ? (
                            renderAttachmentsList()
                        ) : (
                            <Attachments
                                attachments={attachments}
                                getFileName={(file: T) => {
                                    if (isFileWithNestedName(file)) return file.file.name;
                                    if (isHasName(file)) return file.name;
                                    return 'default_name';
                                }}
                            />
                        )}
                        {attachmentsData.length > 0 && !isFileOver && !renderAttachmentsList && (
                            <li onClick={clickFileInput} className={cls.AddFileBtn}>
                                <Icon
                                    iconName="fileInput"
                                    className={cls.fileInputIcon}
                                    width={26}
                                    height={26}
                                />
                                <span className={cn(cls.AddFileTitle)}>
                                    {t('Tasks.ModalFileUploader.DnD.AddFile.text')}
                                </span>
                            </li>
                        )}
                    </div>
                )}
                {/* )} */}
                {isFileOver && attachments.length > 0 && !isDisabled && (
                    <span className={cls.DragOverTip}>
                        {t('Tasks.ModalFileUploader.dragOverTip.title')}
                    </span>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    multiple={multiple}
                    onChange={handleInputChange}
                    accept={accept}
                />
            </div>
        </div>
    );
};
